import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnDestroy, Optional, Self, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

import { fuseConfig } from 'app/fuse-config/index';
import { FuseConfig } from '@fuse/types';

/** Data structure for holding telephone number. */
export class TelNum {
    constructor(public country: string, public provider: string, public phone: string) { }
}

/** Custom `MatFormFieldControl` for telephone number input. */
@Component({
    selector: 'tel-input',
    templateUrl: 'tel-input.component.html',
    styleUrls: ['tel-input.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: TelInputComponent }],
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class.floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    }
})
export class TelInputComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnDestroy {

    static nextId = 0;
    static ngAcceptInputType_readonly: boolean | string | null | undefined;
    static ngAcceptInputType_disabled: boolean | string | null | undefined;
    static ngAcceptInputType_required: boolean | string | null | undefined;

    private _placeholder: string;
    private _required = false;
    private _disabled = false;
    private _readonly = false;
    private _fuseConfig: FuseConfig;

    parts: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    // errorState = false;
    controlType = 'tel-input';
    id = `tel-input-${TelInputComponent.nextId++}`;
    describedBy = '';
    countryCodes = [];
    phonePrefixes = [];

    onChange = (_: any) => { };
    onTouched = () => { };

    get empty(): boolean {
        const { value: { country, provider, phone } } = this.parts;

        return !country && !provider && !phone;
    }

    get shouldLabelFloat(): boolean { return this.focused || !this.empty; }

    @Input()
    get errorState(): boolean {
        return this.ngControl.errors !== null && !!this.ngControl.touched;
    }

    @Input()
    get placeholder(): string { return this._placeholder; }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean { return this._required; }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input()
    get readonly(): boolean { return this._readonly; }
    set readonly(value: boolean) {
        this._readonly = coerceBooleanProperty(value);
        this._readonly ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean { return this._disabled; }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }

    @Input()
    get value(): string {
        if (this.parts.valid) {
            const { value: { country, provider, phone } } = this.parts;
            console.log(true);
            return country + provider + phone;
        }
        console.log(false);
        return null;
    }
    set value(tel: string) {
        let countryCode = this.parts.get('country').value || '';
        let providerCode = this.parts.get('provider').value || '';
        let phoneNum = this.parts.get('phone').value || '';
        console.log(providerCode);
        if (tel) {

            if ((countryCode + providerCode + phoneNum).length > 7) {
                tel = countryCode + providerCode + phoneNum;
            }

            if (this.countryCodes.indexOf(tel.substr(0, 2)) > -1) {
                countryCode = tel.substr(0, 2);
                providerCode = this.phonePrefixes[countryCode].indexOf(tel.substr(2, 2)) > -1 ? tel.substr(2, 2) : '';
                phoneNum = tel.substr(4, tel.length);

                if (this.countryCodes.indexOf(tel.substr(0, 3)) > -1) {
                    countryCode = tel.substr(0, 3);
                    providerCode = this.phonePrefixes[countryCode].indexOf(tel.substr(3, 2)) > -1 ? tel.substr(3, 2) : '';
                    phoneNum = tel.substr(5, tel.length);
                }

            } else {

                if (tel.length <= 7 && this.countryCodes.indexOf(tel.substr(0, 2)) === -1) {
                    countryCode = '';
                    providerCode = '';
                    phoneNum = tel;
                } else {
                    countryCode = tel.substr(0, 3);
                    if (this.phonePrefixes[countryCode]) {
                        providerCode = this.phonePrefixes[countryCode].indexOf(tel.substr(3, 2)) > -1 ? tel.substr(3, 2) : '';
                        phoneNum = this.phonePrefixes[countryCode].indexOf(tel.substr(3, 2)) > -1 ? tel.substr(5, tel.length) : tel.substr(3, tel.length);
                    } else {
                        countryCode = '';
                        providerCode = '';
                        phoneNum = tel;
                    }

                }
            }

            console.log('providerCode2: ' + providerCode);

        }
        console.log(countryCode + '-' + providerCode + '-' + phoneNum);
        // console.log(tel.length);

        // const { country, provider, phone } = tel || new TelNum('', '', '');
        // this.parts.setValue({ country, provider, phone });
        this.parts.controls['country'].setValue(countryCode);
        this.parts.controls['provider'].setValue(providerCode);
        this.parts.controls['phone'].setValue(phoneNum);
        this.stateChanges.next();
    }

    constructor(
        formBuilder: FormBuilder,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,
        @Optional() @Self() public ngControl: NgControl) {

        this._fuseConfig = fuseConfig;

        this.countryCodes = Object.keys(this._fuseConfig.settings.phonePrefixes);
        this.phonePrefixes = this._fuseConfig.settings.phonePrefixes;

        this.parts = formBuilder.group({
            country: [null, [Validators.required, Validators.minLength(2)]],
            provider: [null, [Validators.required, Validators.minLength(2)]],
            phone: [null, [Validators.required, Validators.minLength(4), Validators.minLength(6), Validators.maxLength(7), Validators.pattern(/^([+-]?[0-9]\d*|0)$/)]],
        });

        _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }

    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
        console.log('Click!');
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this._elementRef.nativeElement.querySelector('input').focus();
        }
    }

    writeValue(tel: TelNum | null): void {
        if (tel) {
            this.value = tel.country + tel.provider + tel.phone;
        } else {
            this.value = null;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    _handleInput(): void {
        this.onChange(this.value);
    }

    countryChanged(): void {
        console.log('Provider zeroed.');
        this.parts.controls['provider'].setValue('');
    }

}

